import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { NoteResponse, GetNotesApiResponse } from "../../DataModels/DataModels";
import { API_HTTP, resumeAnalyzerApiEndpoints } from "./endpoints";
import { AuthContext } from "../../context/AuthProvider";
import { User } from "firebase/auth";
import ApiInterface from "./api-interface";

const ONE_HOUR_DURATION_IN_MS = 60 * 60 * 1000;

export interface UseNotesResult {
	notes: NoteResponse[];
	isLoading: boolean;
	isError: boolean;
}

export function useNotes(
	jobId: string,
	enabled: boolean = true
): UseNotesResult {
	const authContext = useContext(AuthContext);
	const user = authContext?.user as User | undefined;
	const userId = user?.uid;

	const { data, isLoading, isError } = useQuery<GetNotesApiResponse>({
		queryKey: ["notes", jobId],
		queryFn: async () => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			return apiClient.get(resumeAnalyzerApiEndpoints.jobNotes, {
				jobId
			});
		},
		staleTime: ONE_HOUR_DURATION_IN_MS,
		enabled: !!userId && enabled
	});

	return {
		notes: data?.notes ?? [],
		isLoading,
		isError
	};
}

export function useAddNote(jobId: string) {
	const authContext = useContext(AuthContext);
	const user = authContext?.user as User | undefined;
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: { text: string }): Promise<NoteResponse> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			return apiClient.post(resumeAnalyzerApiEndpoints.jobNotes, payload, {
				jobId
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["notes", jobId] });
			queryClient.invalidateQueries({ queryKey: ["history", jobId] });
		}
	});
}

export function useDeleteNote(jobId: string) {
	const authContext = useContext(AuthContext);
	const user = authContext?.user as User | undefined;
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (noteId: number): Promise<void> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			return apiClient.delete(
				resumeAnalyzerApiEndpoints.jobNoteDelete,
				{},
				{ jobId, noteId }
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["notes", jobId] });
			queryClient.invalidateQueries({ queryKey: ["history", jobId] });
		}
	});
}
